import { ReparoApi } from './index';

export type ObjectiveAnchor = 'DIP' | 'PRODUCTION_START' | 'MATERIAL_PLACE_BY' | 'FG_PLACE_BY';
export type ObjectiveShape = 'SKU' | 'PRODUCTION' | 'MATERIAL' | 'PURCHASE';
export type InstrumentType = 'FG_PO' | 'WORK_ORDER' | 'MATERIAL_PO' | 'TRANSFER';
export type InstrumentStatus = 'PROPOSED' | 'SAVED' | 'SUPERSEDED' | 'APPROVED' | 'SCHEDULED' | 'PLACED' | 'RECEIVED' | 'CLOSED' | 'CANCELLED';
/** A chain step's status: an instrument status, or superseded when a later run replaced it. */
export type ChainStepStatus = InstrumentStatus | 'SUPERSEDED';
export type LandStatus = 'ON_TIME' | 'LATE_NO_STOCKOUT' | 'STOCKOUT' | 'COVERED' | 'UNSOURCEABLE' | 'NO_DIP';
export type LevelKind = 'DAYS' | 'LEAD_TIME';
export type LevelSpec = { kind: 'DAYS'; days: number } | { kind: 'LEAD_TIME'; plusDays?: number };
export interface LevelPolicy {
    brand: LevelSpec;
    tiers?: Record<string, LevelSpec>;
    skus?: Record<string, LevelSpec>;
}
export type ObjectivePeriodRule =
    | { kind: 'RELATIVE'; offsetDays: number; lengthDays: number }
    | { kind: 'FIXED'; start: string; end: string }
    | { kind: 'LEAD_TIME'; plusDays: number };

export interface VendorCandidate {
    vendorId: string;
    vendorName: string | null;
    /** Days from placing to the item being at the factory: the lane, plus the relay when it lands elsewhere. */
    leadDays: number;
    /** Where the lane ends when that is a brand hub, not the factory; the relay's lead; whether that relay lane is set up. */
    landsAtSiteId?: string | null;
    viaLeadDays?: number;
    viaLaneKnown?: boolean;
    /** "I choose" mode: this candidate only dated the line, at the longest lead; nothing is chosen. */
    provisional?: boolean;
    moq: number | null;
    share: number;
    byRule: boolean;
}

/** One step of a part's chain, derived from its inputs and the instruments it waits on. */
export interface ChainStepRow {
    step: 'MATERIAL' | 'TRANSFER' | 'WORK_ORDER' | 'FG_PO' | 'SFG_ON_HAND';
    instrumentId: string | null;
    itemId: string;
    itemName: string | null;
    unit: string | null;
    vendorId: string | null;
    vendorName: string | null;
    fromSiteId: string | null;
    toSiteId: string | null;
    /** Material: what this part needs of it. Transfer / order: what this part draws from that instrument. Work order: the part's quantity. */
    qty: number;
    /** Material: on the factory floor. */
    available: number;
    /** Material: brought by transfers. */
    movable: number;
    /** Material: short, or brought by orders when an instrument carries it. */
    short: number;
    leadDays: number | null;
    placeBy: string | null;
    lands: string | null;
    tranche: number | null;
    note: string | null;
    status: ChainStepStatus | null;
}

/**
 * Actuals against a saved or approved line, read from stock events, orders and STNs only.
 * PRODUCED = FG inwarded at the factory; INWARDED = FG received from the vendor; RECEIVED = material received; MOVED = STN received.
 */
export interface InstrumentActual {
    kind: 'PRODUCED' | 'INWARDED' | 'RECEIVED' | 'MOVED';
    doneQty: number;
    /** For orders: quantity put on documents in the window; null when the type has no order step. */
    orderedQty: number | null;
    firstDate: string | null;
    lastDate: string | null;
    windowStart: string;
    windowEnd: string;
}

/** A saved copy, as a line of a saved plan or a label on a proposal. */
export interface SavedCopy {
    id: string;
    sku: string;
    type: InstrumentType;
    itemName: string | null;
    vendorName: string | null;
    qty: number;
    placeBy: string;
    landDate: string;
    savedAt: string | null;
    /** The objective period it was saved for; Done counts receipts inside it. */
    savedPeriod: { start: string; end: string } | null;
    /** What the books say got done against it; null when not asked for. */
    doneQty: number | null;
}

/**
 * How a line meets what this objective already saved. SAME / CHANGED: saved for this very
 * period (same instrument, vendor and day). NEW: anything else — a line of another day is a new line.
 */
export type SaveMatch = 'NEW' | 'SAME' | 'CHANGED';

/** Saving: each line against what this objective already saved. */
export interface SavePreview {
    lines: Array<{ instrumentId: string; sku: string; type: InstrumentType; itemName: string | null; vendorName: string | null; placeBy: string; qty: number; match: SaveMatch; saved: SavedCopy | null }>;
    /** Saved lines of this plan the run no longer proposes. */
    dropped: SavedCopy[];
}

export interface SaveResult {
    saved: Array<{ id: string; savedFrom: string }>;
    kept: string[];
    replaced: string[];
    withdrawn: string[];
    refused: Array<{ id: string; reason: string }>;
}

export interface InstrumentRow {
    id: string;
    type: InstrumentType;
    status: InstrumentStatus;
    productId: string;
    sku: string;
    requirementSeq: number;
    itemKind: 'PRODUCT' | 'MATERIAL';
    itemId: string;
    itemName: string | null;
    unit: string | null;
    fromFacilityId: string | null;
    toFacilityId: string | null;
    vendorId: string | null;
    vendorName: string | null;
    vendorCandidates: VendorCandidate[];
    choicePending: boolean;
    leadDays: number | null;
    qty: number;
    tranche: number;
    groupKey: string;
    placeBy: string;
    needBy: string;
    landDate: string;
    dependsOnInstrumentIds: string[];
    dependsOn: Array<{ id: string; type: InstrumentType; itemName: string | null; qty: number; status: InstrumentStatus; landDate: string; documentCode: string | null }>;
    /** For a material line or transfer: every run (or FG order) it feeds, earliest first, with its status. */
    feeds: FeedsRun[];
    /** The SKU requirement this serves: the why, without the SKU view. */
    requirement: { dipDate: string | null; zeroDate: string | null; stockoutDays: number; landStatus: LandStatus; startDate: string | null; landDate: string | null } | null;
    /** Set by hand; a fixed `qty` holds through `until`, then the engine plans from actuals again. */
    pinned: { vendorId?: string; qty?: number; until?: string } | null;
    claimedBy: string | null;
    reason: Record<string, unknown>;
    approvedAt: string | null;
    approvedQty: number | null;
    documentType: string | null;
    documentId: string | null;
    documentCode: string | null;
    expectedLandDate: string | null;
    receivedQty: number;
    earlier?: boolean;
    requirementSnapshot?: Record<string, unknown> | null;
    /** On saved and approved rows: what the books say got done against the line. */
    actual?: InstrumentActual | null;
    /** On proposals: the latest saved copy of the same action (any day), with what got done against it. */
    savedCopy?: SavedCopy | null;
}

/** A run (or FG order) a line feeds, with how much of the line that run draws. */
export interface FeedsRun {
    id: string;
    type: InstrumentType;
    sku: string;
    tranche: number;
    qty: number;
    /** How much of this line that run draws; null when not pegged. */
    inputQty: number | null;
    status: InstrumentStatus;
    placeBy: string;
    landDate: string;
    documentCode: string | null;
}

export interface RequirementSeries {
    from: string;
    demand: number[];
    stock: number[];
    inbound: number[];
    planned: number[];
}

/**
 * One PART of one dip of one SKU. A dip (seq) is answered by one or more parts, each a
 * quantity that starts on one day and lands on one day. Group by productId + seq for the
 * dip; part 1 carries the curve. Part 0 is a dip with no part: covered, or unsourceable.
 */
/** The share of a part that serves one priority tier of channels; priority null = the unranked rest. */
export interface PartTier {
    priority: number | null;
    channelKeys: string[];
    qty: number;
}

export interface RequirementRow {
    id: string;
    productId: string;
    sku: string;
    productName: string | null;
    seq: number;
    /** 1..n, or 0 for a dip with no part (covered or unsourceable). */
    part: number;
    /** How many parts answer the dip. */
    parts: number;
    route: 'BUY' | 'MAKE' | 'MAKE_AS_SFG' | null;
    madeAsProductId: string | null;
    facilityId: string | null;
    onHand: number;
    drr: number;
    coverDays: number | null;
    levelDays: number;
    refillDays: number;
    dipDate: string | null;
    zeroDate: string | null;
    /** Earliest action this part needs. */
    placeBy: string | null;
    /** This part starts. */
    startDate: string | null;
    /** This part lands. */
    landDate: string | null;
    /** Of this part: where it lands. COVERED / UNSOURCEABLE / NO_DIP on a part-0 row. */
    landStatus: LandStatus;
    /** Days out of stock before this part lands. */
    stockoutDays: number;
    /** What the curve asked for at the run's landing. `qty` is what the day let it make; the gap is the next dip's business. */
    requiredQty: number;
    /** This part's quantity. */
    qty: number;
    /** R at this part's landing with the earlier parts on the curve: what the curve asked for there. */
    neededAtLanding: number | null;
    /** What let this part start: null for the floor, a material id, or `__capacity__`. */
    unlockedBy: string | null;
    /** What the next part waits on; null on the last part. */
    gatingMaterialId: string | null;
    longestLeadDays: number;
    /** Which channel tiers this part serves, when the factory's day was allotted by channel priority; null otherwise. */
    tiers?: PartTier[] | null;
    /** This part's work order or FG order. */
    instrumentId: string | null;
    /** This part's chain: its materials and the instruments it waits on, then the part itself. */
    chain: ChainStepRow[];
    /** On part 0 or 1 only. */
    series: RequirementSeries | null;
    /** This part's instruments: the part itself and what it waits on. */
    instruments: InstrumentRow[];
}

export interface ViewRequest {
    anchor: ObjectiveAnchor;
    periodStart: string;
    periodEnd: string;
    shape: ObjectiveShape;
    levelDays?: number;
    refillDays?: number;
    objectiveId?: string;
    leadTimePlusDays?: number;
}

/** A SKU the objective has nothing for in its window, and why. */
export interface IdleSku {
    productId: string;
    sku: string;
    productName: string | null;
    route: string | null;
    facilityId: string | null;
    onHand: number;
    drr: number;
    coverDays: number | null;
    levelDays: number;
    horizonDays: number | null;
    inboundOnBook: number;
    firstInboundDate: string | null;
    dipDate: string | null;
    zeroDate: string | null;
    landStatus: LandStatus;
    coveringLandDate: string | null;
    reason: 'NO_DIP' | 'DIP_AFTER_WINDOW' | 'COVERED' | 'UNSOURCEABLE' | 'LATER' | 'EARLIER';
    next: { seq: number; parts: number; qty: number; placeBy: string; startDate: string | null; landDate: string | null } | null;
}

/** A material the planned runs draw on that needs no order or transfer in this window, and why. */
export interface IdleMaterial {
    materialId: string;
    materialName: string | null;
    unit: string | null;
    sites: string[];
    stage: boolean;
    onHand: number;
    inboundOnBook: number;
    drawInWindow: number;
    runsInWindow: number;
    drawBefore: number;
    drawLater: number;
    shortInWindow: number;
    runsOutOn: string | null;
    reason: 'NO_DRAW' | 'FLOOR_COVERS' | 'SHORT' | 'ORDER_LATER' | 'ON_BOOK';
    next: { id: string; type: InstrumentType; status: InstrumentStatus; qty: number; placeBy: string; landDate: string; vendorName: string | null } | null;
}

export interface ViewResponse {
    runId: string | null;
    runDate: string;
    runAt: string | null;
    preview: boolean;
    anchor: ObjectiveAnchor;
    shape: ObjectiveShape;
    periodStart: string;
    periodEnd: string;
    levelDays: number | null;
    refillDays: number | null;
    skus: RequirementRow[];
    instruments: InstrumentRow[];
    earlier: InstrumentRow[];
    /**
     * Production and FG-order views: dips on or before the window end with nothing that can
     * start inside it. Part 1 (or part 0) of each such dip, so the table can say what the
     * SKU waits on and when its first part starts.
     */
    blocked: RequirementRow[];
    /** Production, FG-order and dip views: the SKUs of the view's kind with nothing in this window, and why. */
    idleSkus: IdleSku[];
    /** Material view: the materials the planned runs draw on with no order or transfer in this window, and why. */
    idleMaterials: IdleMaterial[];
    /** The factories the view's runs sit against, keyed by vendor id and by facility id. */
    factories: Record<string, Factory>;
}

export interface Factory {
    vendorId: string;
    name: string | null;
    /** What the factory can make in a day, from the vendor row; null when not set. */
    capacityPerDay: number | null;
}

export interface DueNow {
    runDate: string;
    runAt: string | null;
    dueToday: InstrumentRow[];
    comingUp: InstrumentRow[];
    choicesPending: InstrumentRow[];
}

export interface SavedObjective {
    id: string;
    sentence: string;
    anchor: ObjectiveAnchor;
    periodRule: ObjectivePeriodRule;
    previewLevels: { levelDays?: number; refillDays?: number } | null;
    /** Material anchor: days of production one order covers from its landing. */
    coverDays: number | null;
    shape: ObjectiveShape;
    onHome: boolean;
    isBuiltin: boolean;
}

export interface ReplenishmentPolicy {
    levelPolicy: LevelPolicy | null;
    refillPolicy: LevelPolicy | null;
    defaultTargetDohDays: number | null;
    tierTargetDohDays: Record<string, number> | null;
    allotmentPolicy: { priority: 'NEED_BY' | 'QTY' | 'DRR' | 'DOH'; tieBreak: 'QTY' | 'DRR' | 'SMALLEST' | 'DOH'; pinned: string[]; byChannel?: boolean };
    factoryRules: Record<string, { splitRuns?: boolean; minRunQty?: number }>;
    horizonDays: number;
    runAfterSync: boolean;
    runEveryDays: number;
    /** From the latest run: the longest chain lead and refill across SKUs, and the horizon that run used. */
    maxLeadDays: number;
    maxRefillDays: number;
    latestHorizonDays: number | null;
    defaultChoiceRule: 'SHORTEST_LEAD' | 'CHEAPEST' | 'SPLIT_BY_SHARE' | 'ASK';
}

export type SourcingRoute = 'MAKE' | 'SFG' | 'BUY';
export interface SourcingRule {
    mode: 'PRIMARY_ONLY' | 'OVERFLOW' | 'SPLIT';
    order: string[];
    ratios?: Record<string, number>;
}
export interface SourceOption {
    id: string;
    name: string;
    leadDays: number;
    capacityPerDay: number | null;
}
export interface RouteChoice {
    productId: string;
    sku: string;
    name: string | null;
    options: Array<{ route: SourcingRoute; detail: string; leadDays: number | null }>;
    current: SourcingRoute | null;
}
export interface SourceChoice {
    productId: string;
    sku: string;
    name: string | null;
    kind: 'FACTORY' | 'FG_VENDOR';
    madeFor: string[];
    options: SourceOption[];
    current: SourcingRule | null;
}
export interface MaterialChoice {
    materialId: string;
    name: string | null;
    unit: string | null;
    usedBy: number;
    options: SourceOption[];
    current: SourcingRule | null;
}
export interface SourcingChoices {
    routes: RouteChoice[];
    sources: SourceChoice[];
    materials: MaterialChoice[];
    unmapped: MaterialChoice[];
}
export interface SourcingPatch {
    routes?: Record<string, SourcingRoute>;
    factories?: Record<string, SourcingRule>;
    vendors?: Record<string, SourcingRule>;
}

export interface InstrumentEvent {
    id: string;
    event: string;
    at: string;
    byUserId: string | null;
    byRunId: string | null;
    byJob: string | null;
    payload: Record<string, unknown> | null;
}

export const getReplenishmentView = async (body: ViewRequest): Promise<ViewResponse> =>
    ReparoApi<any>({ method: 'POST', url: '/replenishment/views', body: JSON.stringify(body) });

export const refreshReplenishmentRun = async (): Promise<{ runId: string | null; stats: Record<string, unknown> }> =>
    ReparoApi<any>({ method: 'POST', url: '/replenishment/runs', body: null });

export const getLatestReplenishmentRun = async (): Promise<{ id: string; runDate: string; startedAt: string; finishedAt: string | null; trigger: string; stats: Record<string, unknown> } | null> =>
    ReparoApi<any>({ method: 'GET', url: '/replenishment/runs/latest', body: null });

export const getReplenishmentDue = async (withinDays = 7): Promise<DueNow> =>
    ReparoApi<any>({ method: 'GET', url: `/replenishment/due?withinDays=${withinDays}`, body: null });

export type CalendarStatusFilter = 'PROPOSED' | 'APPROVED' | 'ALL';
export interface CalendarResponse {
    runDate: string;
    /** The last day the latest run planned; proposed rows cannot exist past it. */
    horizonEnd: string | null;
    rows: InstrumentRow[];
}
/** Instruments by place-by date in the range. Proposed rows exist today → horizon; approved and later rows stay where placed. */
export const getReplenishmentCalendar = async (from: string, to: string, status: CalendarStatusFilter = 'ALL'): Promise<CalendarResponse> =>
    ReparoApi<any>({ method: 'GET', url: `/replenishment/calendar?from=${from}&to=${to}&status=${status}`, body: null });

/** Every part of every dip of one SKU, one row per part. */
/** One run's draw on a material at a site: what the part needs of the line and where it comes from. */
export interface MaterialDraw {
    requirementId: string;
    productId: string;
    sku: string;
    productName: string | null;
    seq: number;
    part: number;
    parts: number;
    runQty: number;
    startDate: string | null;
    landDate: string | null;
    instrumentId: string | null;
    status: InstrumentStatus | null;
    needQty: number;
    stageQty: number;
    floorQty: number;
    movedQty: number;
    orderedQty: number;
    shortQty: number;
    note: string | null;
}

/** A material at a factory: the floor, the stage pieces, the planned draws, the landings, and the day it runs out. */
export interface MaterialDetail {
    runId: string;
    runDate: string;
    materialId: string;
    materialName: string | null;
    unit: string | null;
    /** The factory whose runs draw it. */
    siteId: string;
    /** Where the orders land when that is another site (a same-site store or hub). */
    landsAt: string | null;
    onHand: number;
    inboundOnBook: number;
    firstInboundDate: string | null;
    committedDraw: number;
    stage: Array<{ materialId: string; materialName: string | null; unit: string | null; onHand: number }>;
    series: { from: string; floor: number[]; draw: number[]; landing: number[]; stock: number[] };
    runsOutOn: string | null;
    draws: MaterialDraw[];
    landings: InstrumentRow[];
}

export const getReplenishmentMaterial = async (materialId: string, siteId: string): Promise<MaterialDetail> =>
    ReparoApi<any>({ method: 'GET', url: `/replenishment/materials/${encodeURIComponent(materialId)}?siteId=${encodeURIComponent(siteId)}`, body: null });

export const getReplenishmentSku = async (productId: string): Promise<RequirementRow[]> =>
    ReparoApi<any>({ method: 'GET', url: `/replenishment/skus/${encodeURIComponent(productId)}`, body: null });

export type ApprovalGroupBy = 'DAY' | 'MONTH';
export interface ApprovalLineRun {
    instrumentId: string;
    sku: string;
    runStart: string;
    dipDate: string | null;
    zeroDate: string | null;
    runStatus: InstrumentStatus;
    inThisApproval: boolean;
}
export interface ApprovalLine {
    itemId: string;
    itemName: string | null;
    unit: string | null;
    qty: number;
    forSkus: string[];
    forRuns: ApprovalLineRun[];
    instrumentIds: string[];
    placeBy: string;
    landDate: string;
}
/** One document an approval creates: who it goes to, when, and its summed lines. */
export interface ApprovalGroup {
    kind: 'MOTHER_ORDER' | 'PRODUCTION_ORDER' | 'STN';
    key: string;
    partyId: string | null;
    partyName: string | null;
    toFacilityId: string | null;
    orderDate: string;
    landDate: string;
    instrumentIds: string[];
    /** True when a line in it was asked for directly; false when it only came along as an input of a run. */
    asked: boolean;
    lines: ApprovalLine[];
    documentId: string | null;
    documentCode: string | null;
    error?: string;
}
export interface ApprovalDoneLine {
    id: string;
    type: InstrumentType;
    itemName: string | null;
    qty: number;
    status: InstrumentStatus;
    documentCode: string | null;
    landDate: string;
    forSku: string;
}
export interface ApprovalPreview {
    groups: ApprovalGroup[];
    alreadyApproved: ApprovalDoneLine[];
    refused: Array<{ id: string; reason: string }>;
}
export interface ApprovalResult {
    approved: string[];
    refused: Array<{ id: string; reason: string }>;
    documents: ApprovalGroup[];
}

/** The chain the approval would take with it and the documents it would create. Writes nothing. */
export const previewReplenishmentApproval = async (instrumentIds: string[], groupBy: ApprovalGroupBy = 'DAY', objectiveId?: string, excludeIds: string[] = []): Promise<ApprovalPreview> =>
    ReparoApi<any>({ method: 'POST', url: '/replenishment/instruments/approve/preview', body: JSON.stringify({ instrumentIds, objectiveId, groupBy, excludeIds }) });

export const approveReplenishmentInstruments = async (instrumentIds: string[], objectiveId?: string, groupBy: ApprovalGroupBy = 'DAY', excludeIds: string[] = []): Promise<ApprovalResult> =>
    ReparoApi<any>({ method: 'POST', url: '/replenishment/instruments/approve', body: JSON.stringify({ instrumentIds, objectiveId, groupBy, excludeIds }) });

/**
 * Fix a number by hand on a proposed line: `qty` (a run on its day, an FG or material order).
 * The next run reads every pin as input and plans around it; `clear` returns the line to the
 * engine's own number.
 */
/** `until`: the last day a fixed quantity holds (the window it was typed in); after it the engine plans from actuals again. */
export const pinReplenishmentInstrument = async (id: string, body: { vendorId?: string; qty?: number; clear?: boolean; until?: string }): Promise<InstrumentRow> =>
    ReparoApi<any>({ method: 'POST', url: `/replenishment/instruments/${id}/pin`, body: JSON.stringify(body) });

/**
 * Freeze copies of proposed lines as they stand (status SAVED): no document, no pin, no supply;
 * the proposals stay proposed and the next run still re-plans them. Saved copies are read back
 * under Track against what actually got done.
 */
/** A number typed on a dip no run could start in the window: becomes a pinned run (or FG order) on `startDate`, today at the earliest. Re-plan to see it. */
export const pinReplenishmentRequirement = async (body: { productId: string; qty: number; startDate?: string; until?: string }): Promise<InstrumentRow> =>
    ReparoApi<any>({ method: 'POST', url: '/replenishment/requirements/pin', body: JSON.stringify(body) });

export const saveReplenishmentInstruments = async (instrumentIds: string[], objectiveId?: string, period?: { start: string | null; end: string | null }, decisions?: { replaceSavedIds?: string[]; withdrawSavedIds?: string[] }): Promise<SaveResult> =>
    ReparoApi<any>({ method: 'POST', url: '/replenishment/instruments/save', body: JSON.stringify({ instrumentIds, objectiveId, periodStart: period?.start ?? undefined, periodEnd: period?.end ?? undefined, ...decisions }) });

/** What saving these would meet: the same actions already saved for the objective and period. Nothing is written. */
export const previewSaveReplenishmentInstruments = async (instrumentIds: string[], objectiveId?: string, period?: { start: string | null; end: string | null }): Promise<SavePreview> =>
    ReparoApi<any>({ method: 'POST', url: '/replenishment/instruments/save/preview', body: JSON.stringify({ instrumentIds, objectiveId, periodStart: period?.start ?? undefined, periodEnd: period?.end ?? undefined }) });

export const unsaveReplenishmentInstrument = async (id: string): Promise<void> =>
    ReparoApi<any>({ method: 'DELETE', url: `/replenishment/instruments/${id}/saved`, body: null });

export const getReplenishmentInstrumentEvents = async (id: string): Promise<InstrumentEvent[]> =>
    ReparoApi<any>({ method: 'GET', url: `/replenishment/instruments/${id}/events`, body: null });

export const getReplenishmentApprovals = async (from: string, to: string, by: 'DIP' | 'LAND' | 'APPROVED'): Promise<InstrumentRow[]> =>
    ReparoApi<any>({ method: 'GET', url: `/replenishment/approvals?from=${from}&to=${to}&by=${by}`, body: null });

export interface ObjectiveCount {
    objectiveId: string;
    sentence: string;
    shape: ObjectiveShape;
    dueToday: number;
    inPeriod: number;
}
/** Per on-page objective: proposed lines in its window and how many are due today or overdue. */
export const getReplenishmentObjectiveCounts = async (): Promise<ObjectiveCount[]> =>
    ReparoApi<any>({ method: 'GET', url: '/replenishment/objectives/counts', body: null });

export const listReplenishmentObjectives = async (): Promise<SavedObjective[]> =>
    ReparoApi<any>({ method: 'GET', url: '/replenishment/objectives', body: null });

export const saveReplenishmentObjective = async (body: Omit<SavedObjective, 'id' | 'isBuiltin'> & { id?: string }): Promise<SavedObjective> =>
    ReparoApi<any>({ method: 'POST', url: '/replenishment/objectives', body: JSON.stringify(body) });

export const deleteReplenishmentObjective = async (id: string): Promise<void> =>
    ReparoApi<any>({ method: 'DELETE', url: `/replenishment/objectives/${id}`, body: null });

export const getReplenishmentSourcing = async (): Promise<SourcingChoices> =>
    ReparoApi<any>({ method: 'GET', url: '/replenishment/sourcing', body: null });

/** Keys given replace, keys omitted stay. */
export const saveReplenishmentSourcing = async (patch: SourcingPatch): Promise<SourcingPatch> =>
    ReparoApi<any>({ method: 'PUT', url: '/replenishment/sourcing', body: JSON.stringify(patch) });

export const getReplenishmentPolicy = async (): Promise<ReplenishmentPolicy> =>
    ReparoApi<any>({ method: 'GET', url: '/replenishment/policy', body: null });

export const saveReplenishmentPolicy = async (body: Partial<ReplenishmentPolicy>): Promise<ReplenishmentPolicy> =>
    ReparoApi<any>({ method: 'PUT', url: '/replenishment/policy', body: JSON.stringify(body) });
